import _ from 'lodash';

import {
  BothTreatmentPlans,
  NONEVALUE,
  ZenClear,
  ZenClearLite,
  ZenClearUltra,
  ZenPlus
} from 'constants/common';
import { ZENSTAGE_OPTIONS } from 'constants/options';
import { REFERRALS_STEPS, valueKeyMapper } from 'constants/referral';
import {
  ARCHES_TO_TREAT,
  ATTACHMENTS_OPTIONS,
  OCCLUSION_CORRECTION,
  IPR_OR_ATTACHMENT_OPTIONS,
  IPR_REFINEMENT_FORM_OPTIONS,
  MIDLINES,
  OVERBITES,
  REFINEMENT_GOALS,
  REFINEMENT_GOALS_OPTIONS,
  REFINEMENT_IPR,
  RESOLVE_POSTERIOR_OPEN_BITE_OPTIONS,
  TEETH_SELECT_OPTIONS,
  TRUTH_VALUE,
  TYPES,
  ZENCLEAR_TPV_REFINEMENT_FORM_ARCHES_TO_TREAT,
  ZENCLEAR_TPV_REFINEMENT_FORM_ATTACHMENTS,
  ZENCLEAR_TPV_REFINEMENT_FORM_REASON_BACKEND,
  ZENCLEAR_TPV_REFINEMENT_FORM_REASON_WITH_ILL_FITTING
} from 'constants/TPVFormOptions';
import {
  BITE_RAMP,
  IPR_LOCATION,
  IPR_OPTIONS,
  PREMOLAR_CROSSBITE
} from 'constants/treatmentPlanningPreferenceTemplate';
import { FilesData } from 'types/Files.types';
import {
  AlignerProductionType,
  ToothOptionInput,
  TREATMENT_PLAN_TYPE
} from 'types/PatientList.types';
import {
  clinicalPreferencesRefinementType,
  TreatmentSpecExternalType,
  TreatmentSpecRefinementType
} from 'types/PendingPrescription.types';
import { ReferralButton } from 'types/Referrals.types';
import { Option } from 'types/select';
import {
  Preference,
  TreatmentPLanPreferenceFormType
} from 'types/Settings.types';
import {
  ALIGNER_INFO_TYPE,
  AlignerIPRInfo,
  ClinicalPreferencesApiInput,
  RefinementFormType,
  TemplateFormTypes,
  TPVFormTypes,
  ZENCLEAR,
  ZENCLEAR_LITE,
  ZENCLEAR_ULTRA,
  ZenclearAndZenclearLiteApiInput,
  ZENPLUS,
  ZenPlusApiInput
} from 'types/TPVForm.types';
import {
  midlineMapper,
  midlineShiftsMapper,
  overBiteValueMapper,
  PriorTreatment,
  PriorTreatmentNeededType,
  TeethExtraction,
  TreatmentPlanning,
  TreatmentSpecificationOptions,
  TreatmentSpecifications,
  TreatmentSpecType
} from 'types/TreatmentPlanning.types';
import { UltraRefinementTpvType } from 'types/UltraRefinementPrescription.types';
import { translate } from 'utils';
import {
  buildUltraRefinementTpvPayload,
  getUltraRefinementPrescriptionSavedValues,
  hasUltraRefinementSubmissionReason
} from 'utils/ultraRefinementPrescription.utils';

import {
  formatMidlinesShiftForForm,
  formatTeeth,
  getAlignerData,
  getArchesToTreat,
  getRefinementFormData,
  getSetAndZenStageDefaultValues,
  getTreatmentGoalsAndProcedures,
  getZenClearDataForTreatmentSpec,
  getZenPlusDataForTreatmentSpec
} from './tpvUtils';
import {
  formatUltraForForm,
  getDefaultZenClearUltraTreatmentSpec,
  getZenClearUltraDataForTreatmentSpec
} from './ultraPrescription.utils';
import { treatmentPlanType } from '../components/TreatmentPlanningInstructions/TreatmentPlanningInstructions.config';
import { EnumResponse } from '../types/TreatmentPlanning.types';

type ErrorList = { id: string; label: string; field: string };

export const formatTeethsForSubmission = (
  teeths: TeethExtraction | undefined,
  instruction: string | undefined
): string | undefined => {
  if (instruction === 'NO_TEETH_TO_EXTRACT') return NONEVALUE;
  else if (instruction === 'TEETH_TO_EXTRACT') {
    if (!teeths) return NONEVALUE;
    const teethsArray = Object.entries(teeths).reduce((prev, [, value]) => {
      value?.length &&
        prev.push(
          value
            ?.map((option) => option.value)
            .sort()
            .toString()
        );
      return prev;
    }, [] as string[]);
    return teethsArray.toString();
  }
  return undefined;
};

export const formatTeethsForController = (
  teeths: string | undefined | null
): TeethExtraction | undefined => {
  if (teeths === NONEVALUE || !teeths) return undefined;
  else {
    const [rightTop, rightBottom, leftTop, leftBottom] = teeths
      .split(',')
      .reduce(
        (prev, current) => {
          const teethPostion = returnTeethPostion(current);
          prev[teethPostion].push({ value: current });
          return prev;
        },
        [[], [], [], []] as Option[][]
      );
    return { rightTop, rightBottom, leftTop, leftBottom };
  }
};

const formatIprOrAttachment = (iprOrAttachment?: string[]) => {
  return IPR_OR_ATTACHMENT_OPTIONS.filter((option: Option) =>
    iprOrAttachment?.includes(option.value)
  );
};
export const formatTeethSelection = (teeths: string | undefined) => {
  if (!teeths) return '';
  else if (teeths === TEETH_SELECT_OPTIONS.NONE)
    return TEETH_SELECT_OPTIONS.NONE;
  else return TEETH_SELECT_OPTIONS.SELECT_TEETH;
};

const returnTeethPostion = (teeth: string) => {
  switch (teeth.charAt(0)) {
    case '1':
      return 0;
    case '2':
      return 2;
    case '3':
      return 3;
    case '4':
      return 1;
    default:
      return 0;
  }
};

export const formatTeethToExtract = (
  extractTeeth?: string,
  teethtoExtract?: TeethExtraction
) => {
  if (extractTeeth === TEETH_SELECT_OPTIONS.NONE)
    return TEETH_SELECT_OPTIONS.NONE;
  if (extractTeeth === TEETH_SELECT_OPTIONS.SELECT_TEETH)
    return formatTeeth(teethtoExtract);
  if (!extractTeeth) return undefined;
};

const formatTreatmentPlanForSubmission = (
  treatmentPlanTypes: string | undefined
) => {
  const isBothSelected = treatmentPlanTypes === BothTreatmentPlans;
  if (isBothSelected)
    return treatmentPlanType
      .filter(({ value }) => value !== BothTreatmentPlans)
      ?.map(({ value }) => value);
  return treatmentPlanTypes ? [treatmentPlanTypes] : [];
};

export const getDefaultTreatmentPlanType = (
  doctorComplexity: number | null
) => {
  if (doctorComplexity && doctorComplexity < 4) return ZenClear;
  return '';
};

const getZenplusInstruction = (
  inst: string | undefined,
  fromReferral: boolean | undefined
) => {
  const instruction = fromReferral ? inst : inst || undefined;
  return instruction;
};

export const getFiles = (
  filetype: string,
  documents?: FilesData[]
): FilesData[] => {
  return documents?.filter((docs) => docs.key === filetype) || [];
};

export const formatTreatmentPlaningForSubmission = (
  isAutoSave: boolean,
  priorTreatments?: PriorTreatmentNeededType,
  treatmentSpecification?: TreatmentSpecType,
  fromReferral?: boolean
): TreatmentPlanning => {
  const treatmentSpecifications: TreatmentSpecifications = {
    attachmentRequired:
      treatmentSpecification?.treatmentSpecAttachments || undefined,
    crowdingResolvement:
      treatmentSpecification?.crowdingResolvement || undefined,
    iprLocation: treatmentSpecification?.iprLocation || undefined,
    teethToExtract: formatTeethsForSubmission(
      treatmentSpecification?.teethToExtract,
      treatmentSpecification?.teethExtractionInstructions
    ),
    treatmentPlanInstructions:
      treatmentSpecification?.treatmentPlanTypes === ZenClear ||
      treatmentSpecification?.treatmentPlanTypes === BothTreatmentPlans
        ? treatmentSpecification?.treatmentPlanInstructions
        : undefined,
    treatmentPlanTypes:
      formatTreatmentPlanForSubmission(
        treatmentSpecification?.treatmentPlanTypes
      ) || [],
    zenclearLiteInstructions:
      treatmentSpecification?.treatmentPlanTypes === ZenClearLite
        ? treatmentSpecification?.zenclearLiteInstructions
        : undefined,
    zenplusInstructions:
      treatmentSpecification?.treatmentPlanTypes === ZenPlus ||
      treatmentSpecification?.treatmentPlanTypes === BothTreatmentPlans
        ? getZenplusInstruction(
            treatmentSpecification?.zenplusInstructions,
            fromReferral
          )
        : undefined,
    version: !isAutoSave ? 'V0' : undefined
  };
  const priorTreatment: PriorTreatment = {
    canPlanningProceed: priorTreatments?.canPlanningProceed ?? undefined,
    completed: priorTreatments?.completed ?? undefined,
    treatments: priorTreatments?.treatments
      ? priorTreatments?.treatments.filter((value) => value)
      : undefined
  };
  return { priorTreatment, treatmentSpecifications };
};
const getTpvPrescription = (
  isTreatmentTypeChanged: boolean,
  ClinicalPreferences: TreatmentPLanPreferenceFormType,
  treatmentSpecification: TreatmentSpecExternalType,
  includeOcclusionCorrectionFields = true
) => {
  if (treatmentSpecification?.treatmentType === ZENCLEAR)
    return getZenClearDataForTreatmentSpec(
      isTreatmentTypeChanged,
      ClinicalPreferences,
      treatmentSpecification?.ZenClear,
      treatmentSpecification?.treatmentType,
      includeOcclusionCorrectionFields
    );
  else if (treatmentSpecification?.treatmentType === ZENCLEAR_LITE)
    return getZenClearDataForTreatmentSpec(
      isTreatmentTypeChanged,
      ClinicalPreferences,
      treatmentSpecification?.ZenClearLite,
      treatmentSpecification?.treatmentType,
      includeOcclusionCorrectionFields
    );
  else if (treatmentSpecification?.treatmentType === ZENPLUS)
    return getZenPlusDataForTreatmentSpec(
      isTreatmentTypeChanged,
      ClinicalPreferences,
      treatmentSpecification?.ZenPlus,
      includeOcclusionCorrectionFields
    );
  else if (treatmentSpecification?.treatmentType === ZENCLEAR_ULTRA) {
    // Ultra is only valid for braces-templatized prescriptions (same gate as
    // includeOcclusionCorrectionFields from callers).
    if (!includeOcclusionCorrectionFields) {
      return {
        clinicalPreferences: {}
      };
    }
    return getZenClearUltraDataForTreatmentSpec(
      isTreatmentTypeChanged,
      ClinicalPreferences,
      treatmentSpecification?.ZenClearUltra
    );
  } else
    return {
      //RESET case //here clinical preference is sent an empty object when the treatmentType is changed.This is only for SavePrescription.
      clinicalPreferences: {}
    };
};
export const getTreatmentPlanTypes = (
  treatmentType?: string,
  allowZenClearUltra = true
) => {
  if (treatmentType === ZENCLEAR) return [TPVFormTypes.ZENCLEAR];
  else if (treatmentType === ZENPLUS) return [TPVFormTypes.ZENPLUS];
  else if (treatmentType === ZENCLEAR_LITE) return [TPVFormTypes.ZENCLEAR_LITE];
  else if (treatmentType === ZENCLEAR_ULTRA && allowZenClearUltra)
    return [TPVFormTypes.ZENCLEAR_ULTRA];
  return [];
};

export const formatTreatmentPlaningForSubmissionExternal = (
  isTreatmentTypeChanged: boolean,
  isAutosave: boolean,
  treatmentSpecification: TreatmentSpecExternalType,
  ClinicalPreferences: TreatmentPLanPreferenceFormType,
  priorTreatments?: PriorTreatmentNeededType,
  includeOcclusionCorrectionFields = true
): TreatmentPlanning => {
  const treatmentSpecifications: TreatmentSpecifications = {
    treatmentPlanTypes: getTreatmentPlanTypes(
      treatmentSpecification?.treatmentType,
      includeOcclusionCorrectionFields
    ),
    tpv: getTpvPrescription(
      isTreatmentTypeChanged,
      ClinicalPreferences,
      treatmentSpecification,
      includeOcclusionCorrectionFields
    ),
    version: !isAutosave ? 'V1' : undefined
  };
  const priorTreatment: PriorTreatment = {
    canPlanningProceed: priorTreatments?.canPlanningProceed ?? undefined,
    completed: priorTreatments?.completed ?? undefined,
    treatments: priorTreatments?.treatments
      ? priorTreatments?.treatments.filter((value) => value)
      : undefined
  };
  return { priorTreatment, treatmentSpecifications };
};
const getTeethExtractionInst = (teethToExtract: string | undefined) => {
  if (teethToExtract === NONEVALUE) return 'NO_TEETH_TO_EXTRACT';
  else if (teethToExtract) return 'TEETH_TO_EXTRACT';
  return '';
};

const getEmptyTreatmentSpecificationsForControllers =
  (): TreatmentSpecType => ({
    treatmentSpecAttachments: 'IF_NECESSARY',
    crowdingResolvement: 'EXPANSION_WITH_IPR',
    iprLocation: 'AS_REQUIRED',
    teethToExtract: undefined,
    teethExtractionInstructions: '',
    treatmentPlanInstructions: '',
    zenclearLiteInstructions: '',
    treatmentPlanTypes: '',
    zenplusInstructions: ''
  });

export const formatTreatmentSpecificationsForControllers = (
  treatmentSpecifications: TreatmentSpecifications | undefined,
  doctorComplexity?: number,
  isCompleted?: boolean,
  allowZenClearUltra = true
): TreatmentSpecType => {
  // Ultra is not available on non-templatized prescriptions — blank the
  // form so the doctor must reselect Clear / Plus / Lite.
  const savedPlanType = treatmentSpecifications?.treatmentPlanTypes?.[0];
  if (!allowZenClearUltra && isZenClearUltraTreatmentPlanValue(savedPlanType)) {
    return getEmptyTreatmentSpecificationsForControllers();
  }

  let tplan;
  if (
    treatmentSpecifications?.treatmentPlanTypes?.length === 2 &&
    !treatmentSpecifications?.treatmentPlanTypes.includes(
      TPVFormTypes.ZENCLEAR_LITE
    )
  )
    tplan = BothTreatmentPlans;
  else if (doctorComplexity && doctorComplexity <= 3) tplan = ZenClear;
  else tplan = savedPlanType;

  return {
    treatmentSpecAttachments:
      treatmentSpecifications?.attachmentRequired || 'IF_NECESSARY',
    crowdingResolvement:
      treatmentSpecifications?.crowdingResolvement || 'EXPANSION_WITH_IPR',
    iprLocation: treatmentSpecifications?.iprLocation || 'AS_REQUIRED',
    teethToExtract: treatmentSpecifications?.teethToExtract
      ? formatTeethsForController(treatmentSpecifications?.teethToExtract)
      : formatTeethsForController(treatmentSpecifications?.tpv?.teethToExtract),
    teethExtractionInstructions: treatmentSpecifications?.teethToExtract
      ? getTeethExtractionInst(treatmentSpecifications?.teethToExtract)
      : getTeethExtractionInst(treatmentSpecifications?.tpv?.teethToExtract),
    treatmentPlanInstructions:
      migrateNewPrescriptionData(
        TPVFormTypes.ZENCLEAR,
        treatmentSpecifications,
        isCompleted
      ) || '',
    zenclearLiteInstructions:
      migrateNewPrescriptionData(
        TPVFormTypes.ZENCLEAR_LITE,
        treatmentSpecifications,
        isCompleted
      ) || '',
    treatmentPlanTypes: tplan,
    zenplusInstructions:
      migrateNewPrescriptionData(
        TPVFormTypes.ZENPLUS,
        treatmentSpecifications,
        isCompleted
      ) || ''
  };
};

const getTranslatedText = (value?: string) => {
  if (value && valueKeyMapper[value]) {
    return translate(valueKeyMapper[value]);
  }
  return value || '-';
};

const generateSpecialNotes = (value: string, label: string) => {
  const note = '';
  if (value && value !== '-') {
    return note + `\n${label} : ${value}`;
  }
  return '';
};

const migrateOldPrescriptionData = (
  treatmentSpecifications: TreatmentSpecifications,
  isCompleted: boolean,
  treatmentType: TPVFormTypes,
  isExternal?: boolean
) => {
  const data = treatmentSpecifications?.tpv as ZenclearAndZenclearLiteApiInput;
  if (isCompleted || data?.specialNotes?.trim() || isExternal) {
    return data?.specialNotes || '';
  } else {
    const attachment = getTranslatedText(
      treatmentSpecifications?.attachmentRequired
    );
    const crowdingResolvement = getTranslatedText(
      treatmentSpecifications?.crowdingResolvement
    );
    const iprLocation = getTranslatedText(treatmentSpecifications?.iprLocation);
    let treatmentInstruction = '';
    const specialNotes =
      generateSpecialNotes(attachment, 'Attachments') +
      generateSpecialNotes(crowdingResolvement, 'Resolve Crowding') +
      generateSpecialNotes(iprLocation, 'IPR Location');
    if (
      treatmentType === TPVFormTypes.ZENCLEAR &&
      treatmentSpecifications?.treatmentPlanTypes?.includes(
        treatmentType || ''
      ) &&
      treatmentSpecifications?.treatmentPlanInstructions
    ) {
      treatmentInstruction = `Treatment plan instructions: ${treatmentSpecifications?.treatmentPlanInstructions} `;
    }
    // need to change treatment plan instructions for ZenClearLite
    if (
      treatmentType === TPVFormTypes.ZENCLEAR_LITE &&
      treatmentSpecifications?.treatmentPlanTypes?.includes(
        treatmentType || ''
      ) &&
      treatmentSpecifications?.treatmentPlanInstructions
    ) {
      treatmentInstruction = `Treatment plan instructions: ${treatmentSpecifications?.treatmentPlanInstructions} `;
    } else if (
      treatmentType === TPVFormTypes.ZENPLUS &&
      treatmentSpecifications?.treatmentPlanTypes?.includes(
        treatmentType || ''
      ) &&
      treatmentSpecifications?.zenplusInstructions
    ) {
      treatmentInstruction = `Treatment plan instructions: ${treatmentSpecifications?.zenplusInstructions}`;
    }
    return `${treatmentInstruction} ${specialNotes}`.trim();
  }
};

const getFormattedRefinementGoals = (refinementGoals?: ToothOptionInput[]) => {
  const closeResidualSpace: string[] = [];
  const resolvePosteriorOpenBite: string[] = [];
  const info: string[] = [];
  refinementGoals?.forEach((item) => {
    if (
      item?.name === 'Alignment' ||
      item?.name === 'Resolve Heavy Anterior Contacts'
    )
      info.push(`Refinement goal is ${item?.name}`);

    if (item?.name === 'Close Residual Space') {
      item?.value?.forEach((toothData) => {
        closeResidualSpace.push(`${toothData?.position}[${toothData?.value}]`);
      });
      if (closeResidualSpace?.length > 0)
        info.push(
          `Refinement goal is Close Residual Space: ${closeResidualSpace.join(
            ','
          )}`
        );
      else info.push(`Refinement goal is Close Residual Space`);
    }

    if (item?.name === 'Resolve posterior open bite') {
      item?.value?.forEach((toothData) => {
        if (toothData?.name === 'See resulting plan')
          resolvePosteriorOpenBite.push(`${toothData?.name}`);
        else {
          if (toothData?.name && toothData?.value === '')
            resolvePosteriorOpenBite.push(`${toothData?.name}`);
          else if (toothData?.name && !!toothData?.value)
            resolvePosteriorOpenBite.push(
              `${toothData?.name}-${toothData?.value}`
            );
        }
      });
      if (resolvePosteriorOpenBite?.length > 0)
        info.push(
          `Refinement goal is Resolve posterior open bite : ${resolvePosteriorOpenBite.join(
            ','
          )}`
        );
      else info.push(`Refinement goal is Resolve posterior open bite`);
    }
  });
  return info?.length > 0 ? `\n\n${info.join('\n')}` : '';
};

const getFormattedIprInfo = (
  treatmentSpecifications?: TreatmentSpecifications
) => {
  const iprInfo = treatmentSpecifications?.tpv?.iprInfo;
  const iprInfoText = treatmentSpecifications?.tpv?.iprInfoText;
  const value: string[] = [];
  iprInfo?.value?.forEach(
    (item: { value: string; position: string; setNumber: string }) => {
      if (item?.value || item?.position || item?.setNumber)
        value.push(
          `[value:${item?.value},position:${item?.position},set Number:${item?.setNumber}]`
        );
    }
  );
  if (iprInfo?.name === 'IPR performed as prescribed')
    return `\n\nIPR performed:IPR performed as prescribed`;
  if (iprInfo?.name === 'IPR not performed as prescribed') {
    if (!iprInfoText) {
      if (value?.length > 0)
        return `\n\nIPR performed:IPR not performed as prescribed-${value?.join(
          ','
        )}`;
      else return `\n\nIPR performed:IPR not performed as prescribed`;
    } else {
      return `\n\nIPR performed:IPR not performed as prescribed\nIPR info text:${iprInfoText}`;
    }
  }
  return '';
};

const getFormattedAlignerInfo = (
  predecessorTPVData: AlignerIPRInfo,
  alignerInfo?: { name: string; value: { name: string; value: string }[] }[]
) => {
  const alignerData: string[] = [];
  let alignerValueData: string[] = [];
  alignerInfo?.map((item) => {
    alignerValueData = [];
    item?.value?.map((itemValue) => {
      if (itemValue?.name === 'Zenstage') {
        if (itemValue?.value)
          alignerValueData.push(`zenstage number ${itemValue?.value || '-'}`);
        else alignerValueData.push(`zenstage`);
      } else if (itemValue?.name === 'Set') {
        if (itemValue?.value)
          alignerValueData.push(
            `set number ${itemValue?.value || '-'}/${
              predecessorTPVData?.alignerInfo?.setAlignerCount
            }`
          );
        else alignerValueData.push(`set`);
      }
    });
    alignerData.push(`${item?.name} ${alignerValueData?.join(',')}`);
  });
  return alignerData?.length > 0
    ? `\n\nPatient is currently on:\n${alignerData.join('\n')}`
    : '';
};

const getFormattedTreatmentGoals = (
  isZenPlus: boolean,
  treatmentGoals?: {
    name: string;
    procedures?: { name: string; value?: { name: string; value: string }[] }[];
  }[]
) => {
  const procedures: string[] = [];
  const procedureSubOptions: string[] = [];
  let message;
  treatmentGoals?.forEach((goal) => {
    if (goal?.name === 'SPACE_CLOSURE')
      message = `Treatment Goals: Space Closure`;
    else {
      goal?.procedures?.forEach((item) => {
        procedures.push(item?.name);
        if (item?.name === 'DISTALIZATION') {
          item?.value?.forEach((subOption) => {
            if (subOption?.name && !subOption?.value)
              procedureSubOptions?.push(`${subOption?.name}`);
            else if (!!subOption?.name && !!subOption?.value)
              procedureSubOptions?.push(
                `${subOption?.name}- ${subOption?.value}`
              );
          });
        }
      });

      if (goal?.name === 'ALIGNMENT') {
        if (isZenPlus)
          message = `Treatment Goals: Alignment-${procedures.join(',')}`;
        else message = `Treatment Goals: Alignment`;
      }
      if (goal?.name === 'RETRACTION') {
        if (isZenPlus) {
          if (procedures?.length > 0 && procedureSubOptions?.length > 0)
            message = `Treatment Goals: Retraction-${procedures.join(
              ','
            )}:${procedureSubOptions.join('|')}`;
          else if (procedures?.length > 0 && procedureSubOptions?.length === 0)
            message = `Treatment Goals: Retraction-${procedures.join(',')}`;
          else message = `Treatment Goals: Retraction`;
        } else message = `Treatment Goals: Retraction`;
      }
    }
  });
  return message ? `\n\n${message}` : '';
};

const getFormattedMidlines = (midlines?: string, midlineShifts?: string[]) => {
  const shifts: string[] = [];
  if (midlines) {
    if (midlines === 'IMPROVE') {
      midlineShifts?.forEach((item) => {
        shifts?.push(midlineShiftsMapper[item]);
      });
      if (shifts?.length > 0)
        return `\n\nMidlines: ${midlineMapper[midlines || '']}-${shifts.join(
          ','
        )}`;
      else return `\n\nMidlines: ${midlineMapper[midlines || '']}`;
    }
    return `\n\nMidlines: ${midlineMapper[midlines || '']}`;
  }
  return '';
};

const getFormattedProsthesis = (
  prosthesis?: string,
  prosthesisOptions?: string[],
  prosthesisIprToAvoid?: string,
  prosthesisAttachmentToAvoid?: string
) => {
  const restorations: string[] = [];
  if (prosthesis === 'NO') return `\n\nProsthesis: None`;
  else if (prosthesis === 'YES') {
    if (prosthesisOptions?.includes('IPR to avoid')) {
      if (prosthesisIprToAvoid)
        restorations.push(`IPR to avoid: ${prosthesisIprToAvoid || '-'}`);
      else restorations.push(`IPR to avoid`);
    }
    if (prosthesisOptions?.includes('Attachment to avoid')) {
      if (prosthesisAttachmentToAvoid)
        restorations.push(
          `Attachment to avoid: ${prosthesisAttachmentToAvoid || '-'}`
        );
      else restorations.push(`Attachment to avoid`);
    }
    if (restorations?.length > 0)
      return `\n\nProsthesis: Yes-${restorations?.join(',')}`;
    else return `\n\nProsthesis: Yes`;
  }
  return '';
};
const getFormattedText = (label?: string, value?: any) => {
  if (value) return `\n\n${label} : ${value}`;
  else return '';
};
const getFormattedToothMovementRestrictions = (teethToMove?: string) => {
  if (!!teethToMove && teethToMove !== 'None')
    return `\n\nTooth movement restrictions: These teeth should not be moved - ${teethToMove}`;
  else if (teethToMove === 'None')
    return '\n\nTooth movement restrictions: None';
  return '';
};
const getFormattedArchesToTreat = (archesToTreat?: string[]) => {
  if (archesToTreat?.length === 1 && archesToTreat[0] === 'UPPER')
    return 'Arches to treat:Upper - passive aligners for opposing arch';
  else if (archesToTreat?.length === 1 && archesToTreat[0] === 'LOWER')
    return 'Arches to treat:Lower - passive aligners for opposing arch';
  else if (archesToTreat?.length === 2) return 'Arches to treat:Both';
  return '';
};
export const migrateNewRefinementPrescriptionData = (
  predecessorTPVData: AlignerIPRInfo,
  treatmentType: TPVFormTypes,
  treatmentSpecifications?: TreatmentSpecifications,
  isCompleted?: boolean,
  isSumbissionReasonAndAlignerInfoNeeded?: boolean
) => {
  const isZenClear = treatmentType === planTypeCode.ZENCLEAR;
  const isZenPlus = treatmentType === planTypeCode.ZEN_PLUS;
  const isZenClearLite = treatmentType === planTypeCode.ZENCLEAR_LITE;
  const tpv = treatmentSpecifications?.tpv;

  if (
    isZenClear &&
    (isCompleted || !!treatmentSpecifications?.treatmentPlanInstructions)
  )
    return treatmentSpecifications?.treatmentPlanInstructions;
  else if (
    isZenPlus &&
    (isCompleted || !!treatmentSpecifications?.zenplusInstructions)
  )
    return treatmentSpecifications?.zenplusInstructions;
  else if (
    isZenClearLite &&
    (isCompleted || !!treatmentSpecifications?.zenclearLiteInstructions)
  )
    return treatmentSpecifications?.zenclearLiteInstructions;
  else {
    return `${getFormattedArchesToTreat(tpv?.archesToTreat)}${getFormattedText(
      'Attachments',
      tpv?.attachments
    )}${getFormattedText(
      'Special Notes',
      tpv?.specialNotes
    )}${getFormattedRefinementGoals(tpv?.refinementGoals)}${getFormattedIprInfo(
      treatmentSpecifications
    )}${
      isSumbissionReasonAndAlignerInfoNeeded
        ? `${getFormattedText(
            'Refinement Submission Reason',
            tpv?.submissionReason
          )}${getFormattedAlignerInfo(predecessorTPVData, tpv?.alignerInfo)}`
        : ''
    }`;
  }
};
export const migrateNewPrescriptionData = (
  treatmentType: TPVFormTypes,
  treatmentSpecifications?: TreatmentSpecifications,
  isCompleted?: boolean
) => {
  const isZenClear = treatmentType === TPVFormTypes.ZENCLEAR;
  const isZenPlus = treatmentType === TPVFormTypes.ZENPLUS;
  const isZenClearLite = treatmentType === TPVFormTypes.ZENCLEAR_LITE;
  const tpv = treatmentSpecifications?.tpv;

  if (
    isZenClear &&
    (isCompleted || !!treatmentSpecifications?.treatmentPlanInstructions)
  )
    return treatmentSpecifications?.treatmentPlanInstructions;
  else if (
    isZenPlus &&
    (isCompleted || !!treatmentSpecifications?.zenplusInstructions)
  )
    return treatmentSpecifications?.zenplusInstructions;
  else if (
    isZenClearLite &&
    (isCompleted || !!treatmentSpecifications?.zenclearLiteInstructions)
  )
    return treatmentSpecifications?.zenclearLiteInstructions;
  else {
    if (
      isZenClear &&
      treatmentSpecifications?.treatmentPlanTypes?.includes(treatmentType)
    ) {
      const treatmentPlanInstructions = `${getFormattedArchesToTreat(
        tpv?.archesToTreat
      )}${getFormattedToothMovementRestrictions(
        tpv?.toothMovementRestrictions || ''
      )}${getFormattedTreatmentGoals(
        false,
        tpv?.treatmentGoals
      )}${getFormattedText(
        'Special Notes',
        tpv?.specialNotes
      )}${getFormattedProsthesis(
        tpv?.prosthesis,
        tpv?.prosthesisOptions,
        tpv?.prosthesisIprToAvoid,
        tpv?.prosthesisAttachmentToAvoid
      )}`;
      return treatmentPlanInstructions;
    }
    if (
      isZenPlus &&
      treatmentSpecifications?.treatmentPlanTypes?.includes(treatmentType)
    ) {
      const zenplusInstructions = `${getFormattedArchesToTreat(
        tpv?.archesToTreat
      )}${getFormattedToothMovementRestrictions(
        tpv?.toothMovementRestrictions || ''
      )}${getFormattedText(
        'Overbite',
        translate(overBiteValueMapper[tpv?.overbite || ''])
      )}${getFormattedText(
        'Special Notes',
        tpv?.specialNotes
      )}${getFormattedTreatmentGoals(
        true,
        tpv?.treatmentGoals
      )}${getFormattedMidlines(
        tpv?.midlines,
        tpv?.midlinesShifts
      )}${getFormattedProsthesis(
        tpv?.prosthesis,
        tpv?.prosthesisOptions,
        tpv?.prosthesisIprToAvoid,
        tpv?.prosthesisAttachmentToAvoid
      )}`;
      return zenplusInstructions;
    }
  }
};
export const formatTreatmentSpecificationsForExternal = (
  treatmentSpecifications?: TreatmentSpecifications,
  isCompleted = false,
  allowZenClearUltra = true
): TreatmentSpecExternalType => {
  let data;
  if (treatmentSpecifications) {
    data = treatmentSpecifications?.tpv as ZenclearAndZenclearLiteApiInput;
    if (
      treatmentSpecifications?.treatmentPlanTypes?.[0] === TPVFormTypes.ZENCLEAR
    ) {
      return {
        ZenClear: {
          treatmentGoals: {
            treatmentGoals: data?.treatmentGoals?.map((item: any) => item.name)
          },

          archesToTreat: getArchesToTreat(data?.archesToTreat),
          toothMovementRestrictions: {
            toothMovementRestrictions: data?.toothMovementRestrictions
              ? formatTeethSelection(data.toothMovementRestrictions)
              : TEETH_SELECT_OPTIONS.NONE,
            teethNotToMove: formatTeethsForController(
              data?.toothMovementRestrictions
            )
          },
          prosthesis: {
            iprOrAttachment: formatIprOrAttachment(data?.prosthesisOptions),
            prosthesis: data?.prosthesis ? data.prosthesis : TRUTH_VALUE.NO,
            prosthesisIprToAvoid: formatTeethsForController(
              data?.prosthesisIprToAvoid
            ),
            prosthesisAttachmentToAvoid: formatTeethsForController(
              data?.prosthesisAttachmentToAvoid
            )
          },
          extractTeeth: {
            extractTeeth:
              data?.teethToExtract || treatmentSpecifications?.teethToExtract
                ? formatTeethSelection(
                    data?.teethToExtract ||
                      treatmentSpecifications?.teethToExtract
                  )
                : TEETH_SELECT_OPTIONS.NONE,
            teethToExtract: formatTeethsForController(
              data?.teethToExtract || treatmentSpecifications?.teethToExtract
            )
          },
          occlusionCorrection:
            data?.occlusionCorrection ?? OCCLUSION_CORRECTION.NONE,
          occlusionCorrectionTeeth: formatTeethsForController(
            data?.occlusionCorrectionInfo
          ),
          specialNotes: migrateOldPrescriptionData(
            treatmentSpecifications,
            isCompleted,
            TPVFormTypes.ZENCLEAR,
            true
          )
        },
        ZenPlus: {
          treatmentGoals: undefined,

          archesToTreat: ARCHES_TO_TREAT.BOTH,
          toothMovementRestrictions: {
            teethNotToMove: undefined,
            toothMovementRestrictions: TEETH_SELECT_OPTIONS.NONE
          },
          prosthesis: {
            iprOrAttachment: undefined,
            prosthesis: TRUTH_VALUE.NO,
            prosthesisIprToAvoid: undefined,
            prosthesisAttachmentToAvoid: undefined
          },
          extractTeeth: {
            extractTeeth: TEETH_SELECT_OPTIONS.NONE,
            teethToExtract: undefined
          },
          occlusionCorrection: OCCLUSION_CORRECTION.NONE,
          occlusionCorrectionTeeth: undefined,
          specialNotes: isCompleted
            ? ''
            : treatmentSpecifications?.zenplusInstructions || '',

          overbite: OVERBITES.SHOW_RESULTING,
          midlines: {
            midlines: MIDLINES.SHOW_RESULTING,
            shiftUpper: undefined,
            shiftLower: undefined,
            shiftUpperPosition: undefined,
            shiftLowerPosition: undefined
          }
        },
        ZenClearUltra: getDefaultZenClearUltraTreatmentSpec(),
        treatmentType: ZENCLEAR
      };
    } else if (
      treatmentSpecifications?.treatmentPlanTypes?.[0] ===
      TPVFormTypes.ZENCLEAR_LITE
    ) {
      return {
        ZenClearLite: {
          treatmentGoals: {
            treatmentGoals: data?.treatmentGoals?.map((item: any) => item.name)
          },

          archesToTreat: getArchesToTreat(data?.archesToTreat),
          toothMovementRestrictions: {
            toothMovementRestrictions: data?.toothMovementRestrictions
              ? formatTeethSelection(data.toothMovementRestrictions)
              : TEETH_SELECT_OPTIONS.NONE,
            teethNotToMove: formatTeethsForController(
              data?.toothMovementRestrictions
            )
          },
          prosthesis: {
            iprOrAttachment: formatIprOrAttachment(data?.prosthesisOptions),
            prosthesis: data?.prosthesis ? data.prosthesis : TRUTH_VALUE.NO,
            prosthesisIprToAvoid: formatTeethsForController(
              data?.prosthesisIprToAvoid
            ),
            prosthesisAttachmentToAvoid: formatTeethsForController(
              data?.prosthesisAttachmentToAvoid
            )
          },
          extractTeeth: {
            extractTeeth:
              data?.teethToExtract || treatmentSpecifications?.teethToExtract
                ? formatTeethSelection(
                    data?.teethToExtract ||
                      treatmentSpecifications?.teethToExtract
                  )
                : TEETH_SELECT_OPTIONS.NONE,
            teethToExtract: formatTeethsForController(
              data?.teethToExtract || treatmentSpecifications?.teethToExtract
            )
          },
          occlusionCorrection:
            data?.occlusionCorrection ?? OCCLUSION_CORRECTION.NONE,
          occlusionCorrectionTeeth: formatTeethsForController(
            data?.occlusionCorrectionInfo
          ),
          specialNotes: migrateOldPrescriptionData(
            treatmentSpecifications,
            isCompleted,
            TPVFormTypes.ZENCLEAR,
            true
          )
        },
        ZenPlus: {
          treatmentGoals: undefined,

          archesToTreat: ARCHES_TO_TREAT.BOTH,
          toothMovementRestrictions: {
            teethNotToMove: undefined,
            toothMovementRestrictions: TEETH_SELECT_OPTIONS.NONE
          },
          prosthesis: {
            iprOrAttachment: undefined,
            prosthesis: TRUTH_VALUE.NO,
            prosthesisIprToAvoid: undefined,
            prosthesisAttachmentToAvoid: undefined
          },
          extractTeeth: {
            extractTeeth: TEETH_SELECT_OPTIONS.NONE,
            teethToExtract: undefined
          },
          occlusionCorrection: OCCLUSION_CORRECTION.NONE,
          occlusionCorrectionTeeth: undefined,
          specialNotes: isCompleted
            ? ''
            : treatmentSpecifications?.zenplusInstructions || '',

          overbite: OVERBITES.SHOW_RESULTING,
          midlines: {
            midlines: MIDLINES.SHOW_RESULTING,
            shiftUpper: undefined,
            shiftLower: undefined,
            shiftUpperPosition: undefined,
            shiftLowerPosition: undefined
          }
        },
        ZenClearUltra: getDefaultZenClearUltraTreatmentSpec(),
        treatmentType: ZENCLEAR_LITE
      };
    } else if (
      treatmentSpecifications?.treatmentPlanTypes?.[0] === 'ZENCLEAR_PLUS'
    ) {
      data = treatmentSpecifications?.tpv as ZenPlusApiInput;
      const { shiftLower, shiftUpper } = formatMidlinesShiftForForm(
        data?.midlinesShifts
      );
      const goalsAndProcedures = getTreatmentGoalsAndProcedures(
        data?.treatmentGoals
      );
      return {
        ZenPlus: {
          treatmentGoals: {
            treatmentGoals: goalsAndProcedures.goals,
            treatmentGoalsProcedures:
              goalsAndProcedures.treatmentGoalsProcedure,
            proceduresInnerFields: goalsAndProcedures.proceduresInnerFields,
            upperLowerProcedures: goalsAndProcedures.upperLowerProcedures
          },

          archesToTreat: getArchesToTreat(data?.archesToTreat),
          toothMovementRestrictions: {
            teethNotToMove: formatTeethsForController(
              data?.toothMovementRestrictions
            ),
            toothMovementRestrictions: data?.toothMovementRestrictions
              ? formatTeethSelection(data.toothMovementRestrictions)
              : TEETH_SELECT_OPTIONS.NONE
          },
          prosthesis: {
            iprOrAttachment: formatIprOrAttachment(data?.prosthesisOptions),

            prosthesis: data?.prosthesis ? data.prosthesis : TRUTH_VALUE.NO,
            prosthesisIprToAvoid: formatTeethsForController(
              data?.prosthesisIprToAvoid
            ),
            prosthesisAttachmentToAvoid: formatTeethsForController(
              data?.prosthesisAttachmentToAvoid
            )
          },
          extractTeeth: {
            extractTeeth:
              data?.teethToExtract || treatmentSpecifications?.teethToExtract
                ? formatTeethSelection(
                    data?.teethToExtract ||
                      treatmentSpecifications?.teethToExtract
                  )
                : TEETH_SELECT_OPTIONS.NONE,
            teethToExtract: formatTeethsForController(
              data?.teethToExtract || treatmentSpecifications?.teethToExtract
            )
          },
          occlusionCorrection:
            data?.occlusionCorrection ?? OCCLUSION_CORRECTION.NONE,
          occlusionCorrectionTeeth: formatTeethsForController(
            data?.occlusionCorrectionInfo
          ),
          specialNotes: migrateOldPrescriptionData(
            treatmentSpecifications,
            isCompleted,
            TPVFormTypes.ZENPLUS,
            true
          ),
          overbite: data?.overbite ? data.overbite : OVERBITES.SHOW_RESULTING,
          midlines: {
            midlines: data?.midlines ? data.midlines : MIDLINES.SHOW_RESULTING,
            shiftUpper: !!shiftUpper,
            shiftLower: !!shiftLower,
            shiftUpperPosition: shiftUpper as string,
            shiftLowerPosition: shiftLower as string
          }
        },
        ZenClear: {
          treatmentGoals: undefined,
          archesToTreat: ARCHES_TO_TREAT.BOTH,
          toothMovementRestrictions: {
            toothMovementRestrictions: TEETH_SELECT_OPTIONS.NONE,
            teethNotToMove: undefined
          },
          prosthesis: {
            iprOrAttachment: undefined,
            prosthesis: TRUTH_VALUE.NO,
            prosthesisIprToAvoid: undefined,
            prosthesisAttachmentToAvoid: undefined
          },
          extractTeeth: {
            extractTeeth: TEETH_SELECT_OPTIONS.NONE,
            teethToExtract: undefined
          },
          occlusionCorrection: OCCLUSION_CORRECTION.NONE,
          occlusionCorrectionTeeth: undefined,
          specialNotes: isCompleted
            ? ''
            : treatmentSpecifications?.treatmentPlanInstructions || ''
        },
        ZenClearUltra: getDefaultZenClearUltraTreatmentSpec(),
        treatmentType: ZENPLUS
      };
    } else if (
      treatmentSpecifications?.treatmentPlanTypes?.[0] ===
      TPVFormTypes.ZENCLEAR_ULTRA
    ) {
      // Ultra is not available on non-templatized prescriptions — blank the
      // form so the doctor must reselect Clear / Plus / Lite.
      if (!allowZenClearUltra) {
        return getEmptyTreatmentSpecificationsExternal();
      }
      return {
        ZenClearUltra: {
          ...formatUltraForForm(
            treatmentSpecifications?.tpv as Record<string, unknown>
          )
        },
        ZenClear: {
          treatmentGoals: undefined,
          archesToTreat: ARCHES_TO_TREAT.BOTH,
          toothMovementRestrictions: {
            toothMovementRestrictions: TEETH_SELECT_OPTIONS.NONE,
            teethNotToMove: undefined
          },
          prosthesis: {
            iprOrAttachment: undefined,
            prosthesis: TRUTH_VALUE.NO,
            prosthesisIprToAvoid: undefined,
            prosthesisAttachmentToAvoid: undefined
          },
          extractTeeth: {
            extractTeeth: TEETH_SELECT_OPTIONS.NONE,
            teethToExtract: undefined
          },
          occlusionCorrection: OCCLUSION_CORRECTION.NONE,
          occlusionCorrectionTeeth: undefined,
          specialNotes: isCompleted
            ? ''
            : treatmentSpecifications?.treatmentPlanInstructions || ''
        },
        ZenClearLite: {
          treatmentGoals: undefined,
          archesToTreat: ARCHES_TO_TREAT.BOTH,
          toothMovementRestrictions: {
            toothMovementRestrictions: TEETH_SELECT_OPTIONS.NONE,
            teethNotToMove: undefined
          },
          prosthesis: {
            iprOrAttachment: undefined,
            prosthesis: TRUTH_VALUE.NO,
            prosthesisIprToAvoid: undefined,
            prosthesisAttachmentToAvoid: undefined
          },
          extractTeeth: {
            extractTeeth: TEETH_SELECT_OPTIONS.NONE,
            teethToExtract: undefined
          },
          occlusionCorrection: OCCLUSION_CORRECTION.NONE,
          occlusionCorrectionTeeth: undefined,
          specialNotes: isCompleted
            ? ''
            : treatmentSpecifications?.zenclearLiteInstructions || ''
        },
        ZenPlus: {
          treatmentGoals: undefined,
          archesToTreat: ARCHES_TO_TREAT.BOTH,
          toothMovementRestrictions: {
            teethNotToMove: undefined,
            toothMovementRestrictions: TEETH_SELECT_OPTIONS.NONE
          },
          prosthesis: {
            iprOrAttachment: undefined,
            prosthesis: TRUTH_VALUE.NO,
            prosthesisIprToAvoid: undefined,
            prosthesisAttachmentToAvoid: undefined
          },
          extractTeeth: {
            extractTeeth: TEETH_SELECT_OPTIONS.NONE,
            teethToExtract: undefined
          },
          occlusionCorrection: OCCLUSION_CORRECTION.NONE,
          occlusionCorrectionTeeth: undefined,
          specialNotes: isCompleted
            ? ''
            : treatmentSpecifications?.zenplusInstructions || '',
          overbite: OVERBITES.SHOW_RESULTING,
          midlines: {
            midlines: MIDLINES.SHOW_RESULTING,
            shiftUpper: undefined,
            shiftLower: undefined,
            shiftUpperPosition: undefined,
            shiftLowerPosition: undefined
          }
        },
        treatmentType: ZENCLEAR_ULTRA
      };
    }
  }
  return getEmptyTreatmentSpecificationsExternal();
};

const getEmptyTreatmentSpecificationsExternal =
  (): TreatmentSpecExternalType => ({
    ZenClear: {
      treatmentGoals: undefined,
      archesToTreat: ARCHES_TO_TREAT.BOTH,
      toothMovementRestrictions: {
        toothMovementRestrictions: TEETH_SELECT_OPTIONS.NONE,
        teethNotToMove: undefined
      },
      prosthesis: {
        iprOrAttachment: undefined,
        prosthesis: TRUTH_VALUE.NO,
        prosthesisIprToAvoid: undefined,
        prosthesisAttachmentToAvoid: undefined
      },
      extractTeeth: {
        extractTeeth: TEETH_SELECT_OPTIONS.NONE,
        teethToExtract: undefined
      },
      occlusionCorrection: OCCLUSION_CORRECTION.NONE,
      occlusionCorrectionTeeth: undefined,
      specialNotes: undefined
    },
    ZenPlus: {
      treatmentGoals: undefined,

      archesToTreat: ARCHES_TO_TREAT.BOTH,
      toothMovementRestrictions: {
        teethNotToMove: undefined,
        toothMovementRestrictions: TEETH_SELECT_OPTIONS.NONE
      },
      prosthesis: {
        iprOrAttachment: undefined,
        prosthesis: TRUTH_VALUE.NO,
        prosthesisIprToAvoid: undefined,
        prosthesisAttachmentToAvoid: undefined
      },
      extractTeeth: {
        extractTeeth: TEETH_SELECT_OPTIONS.NONE,
        teethToExtract: undefined
      },
      occlusionCorrection: OCCLUSION_CORRECTION.NONE,
      occlusionCorrectionTeeth: undefined,
      specialNotes: undefined,
      overbite: OVERBITES.SHOW_RESULTING,
      midlines: {
        midlines: MIDLINES.SHOW_RESULTING,
        shiftUpper: undefined,
        shiftLower: undefined,
        shiftUpperPosition: undefined,
        shiftLowerPosition: undefined
      }
    },
    ZenClearUltra: getDefaultZenClearUltraTreatmentSpec(),
    treatmentType: ''
  });
export const isClinicalPreferencesDefined = (
  clinicalPreference?: ClinicalPreferencesApiInput,
  treatmentPlanType?: TPVFormTypes[] | string[],
  isRefinement?: boolean
) => {
  const isZenClearFieldsDefined = !(
    clinicalPreference?.iprSize === null &&
    clinicalPreference?.iprLocation === null
  );
  const isZenPlusFieldsDefined = !(
    clinicalPreference?.iprSize === null &&
    clinicalPreference?.iprLocation === null &&
    clinicalPreference?.biteRamps === null &&
    clinicalPreference?.premolarCrossbite === null &&
    clinicalPreference?.extrusionElastics === null
  );
  if (clinicalPreference === undefined || clinicalPreference === null)
    return false;
  if (treatmentPlanType?.[0] === TPVFormTypes.ZENCLEAR_ULTRA && isRefinement) {
    return clinicalPreference?.others != null;
  }
  if (
    ((treatmentPlanType?.[0] === TREATMENT_PLAN_TYPE.ZENCLEAR ||
      treatmentPlanType?.[0] === TREATMENT_PLAN_TYPE.ZENCLEAR_LITE) &&
      clinicalPreference !== undefined) ||
    (isRefinement && treatmentPlanType?.[0] !== TPVFormTypes.ZENCLEAR_ULTRA)
  ) {
    return isZenClearFieldsDefined;
  }
  if (
    treatmentPlanType?.[0] === TREATMENT_PLAN_TYPE.ZENCLEAR_PLUS &&
    !isRefinement
  ) {
    return isZenPlusFieldsDefined;
  }
  if (treatmentPlanType?.[0] === TPVFormTypes.ZENCLEAR_ULTRA && !isRefinement) {
    return clinicalPreference?.others != null;
  }
  return false;
};
export const setClinicalPreferenceFromPreferenceTemplate = (
  preferenceTemplateData: Preference,
  isRefinement?: boolean,
  tpvTypeRefinement?: string
) => {
  const iprSizeZenClear = IPR_OPTIONS.filter((option: Option) =>
    preferenceTemplateData?.ZenClear?.iprSize?.includes(option.value)
  );
  const iprLocationZenClear = IPR_LOCATION.filter((option: Option) =>
    preferenceTemplateData?.ZenClear?.iprLocation?.includes(option.value)
  );
  const iprSizeZenPlusRefinement = IPR_OPTIONS.filter((option: Option) =>
    preferenceTemplateData?.ZenPlusRefinement?.iprSize?.includes(option.value)
  );
  const iprLocationZenPlusRefinement = IPR_LOCATION.filter((option: Option) =>
    preferenceTemplateData?.ZenPlusRefinement?.iprLocation?.includes(
      option.value
    )
  );
  const iprSizeZenClearRefinement = IPR_OPTIONS.filter((option: Option) =>
    preferenceTemplateData?.ZenClearRefinement?.iprSize?.includes(option.value)
  );
  const iprLocationZenClearRefinement = IPR_LOCATION.filter((option: Option) =>
    preferenceTemplateData?.ZenClearRefinement?.iprLocation?.includes(
      option.value
    )
  );
  const iprSizeZenPlus = IPR_OPTIONS.filter((option: Option) =>
    preferenceTemplateData?.ZenPlus?.iprSize?.includes(option.value)
  );
  const iprLocationZenPlus = IPR_LOCATION.filter((option: Option) =>
    preferenceTemplateData?.ZenPlus?.iprLocation?.includes(option.value)
  );
  const biteRampZenPlus = BITE_RAMP.filter(
    (option: Option) =>
      preferenceTemplateData?.ZenPlus?.biteRamps === option.value
  );
  const crossbiteZenPlus = PREMOLAR_CROSSBITE.filter(
    (option: Option) =>
      preferenceTemplateData?.ZenPlus?.premolarCrossbite === option.value
  );
  const elasticsZenPlus = BITE_RAMP.filter(
    (option: Option) =>
      preferenceTemplateData?.ZenPlus?.extrusionElastics === option.value
  );
  if (isRefinement) {
    if (tpvTypeRefinement === planTypeCode.ZENCLEAR_ULTRA) {
      return {
        ZenClearUltra: {
          others: preferenceTemplateData?.ZenClearUltraRefinement?.others || ''
        }
      };
    }
    if (
      tpvTypeRefinement === planTypeCode.ZEN_PLUS &&
      preferenceTemplateData?.ZenPlusRefinement
    )
      return {
        Refinement: {
          iprSize: iprSizeZenPlusRefinement,
          iprLocation: iprLocationZenPlusRefinement,
          others: preferenceTemplateData.ZenPlusRefinement.others
        }
      };
    else if (
      tpvTypeRefinement === planTypeCode.ZENCLEAR &&
      preferenceTemplateData?.ZenClearRefinement
    )
      return {
        Refinement: {
          iprSize: iprSizeZenClearRefinement,
          iprLocation: iprLocationZenClearRefinement,
          others: preferenceTemplateData.ZenClearRefinement.others
        }
      };
    else if (tpvTypeRefinement === planTypeCode.ZENCLEAR_LITE) {
      return {
        Refinement: {
          iprSize: [],
          iprLocation: [],
          others: ''
        }
      };
    } else
      return {
        Refinement: undefined
      };
  } else if (
    !!preferenceTemplateData?.ZenClear &&
    !preferenceTemplateData?.ZenPlus &&
    !isRefinement
  ) {
    return {
      ZenClear: {
        iprSize: iprSizeZenClear,
        iprLocation: iprLocationZenClear,
        others: preferenceTemplateData?.ZenClear?.others
      },
      ZenPlus: undefined,
      ZenClearUltra: undefined,
      treatmentPlanType: undefined
    };
  } else if (
    !!preferenceTemplateData?.ZenPlus &&
    !preferenceTemplateData?.ZenClear &&
    !isRefinement
  ) {
    return {
      ZenPlus: {
        iprSize: iprSizeZenPlus,
        iprLocation: iprLocationZenPlus,
        biteRamps: biteRampZenPlus[0],
        premolarCrossbite: crossbiteZenPlus[0],
        extrusionElastics: elasticsZenPlus[0],
        others: preferenceTemplateData?.ZenPlus?.others
      },
      ZenClear: undefined,
      ZenClearUltra: undefined,
      treatmentPlanType: undefined
    };
  } else if (
    !!preferenceTemplateData?.ZenClearUltra &&
    !preferenceTemplateData?.ZenClear &&
    !preferenceTemplateData?.ZenPlus &&
    !isRefinement
  ) {
    return {
      ZenClearUltra: {
        others: preferenceTemplateData?.ZenClearUltra?.others ?? ''
      },
      treatmentPlanType: TPVFormTypes.ZENCLEAR_ULTRA
    };
  }
  return {
    ZenClear: {
      iprSize: iprSizeZenClear,
      iprLocation: iprLocationZenClear,
      others: preferenceTemplateData?.ZenClear?.others
    },
    ZenPlus: {
      iprSize: iprSizeZenPlus,
      iprLocation: iprLocationZenPlus,
      biteRamps: biteRampZenPlus[0],
      premolarCrossbite: crossbiteZenPlus[0],
      extrusionElastics: elasticsZenPlus[0],
      others: preferenceTemplateData?.ZenPlus?.others
    },
    ZenClearUltra: {
      others: preferenceTemplateData?.ZenClearUltra?.others ?? ''
    },
    treatmentPlanType: undefined
  };
};

//function to set Default value for clinicalPreference under PendingPrescription
export const formatClinicalPreferencesForExternal = (
  treatmentSpecifications?: TreatmentSpecifications,
  preferenceTemplateData?: Preference,
  isRefinement?: boolean,
  tpvType?: string,
  allowZenClearUltra = true
): TreatmentPLanPreferenceFormType => {
  let isPreferenceDataDefined;
  if (
    treatmentSpecifications?.tpv === null ||
    !treatmentSpecifications?.tpv ||
    !treatmentSpecifications?.tpv?.clinicalPreferences ||
    treatmentSpecifications?.tpv?.clinicalPreferences === null
  )
    isPreferenceDataDefined = false;
  else {
    isPreferenceDataDefined = isClinicalPreferencesDefined(
      treatmentSpecifications?.tpv?.clinicalPreferences,
      treatmentSpecifications?.treatmentPlanTypes,
      isRefinement
    );
  }
  // Saved Ultra clinical prefs must not prepopulate when Ultra is disallowed.
  if (
    !allowZenClearUltra &&
    !isRefinement &&
    treatmentSpecifications?.treatmentPlanTypes?.[0] ===
      TPVFormTypes.ZENCLEAR_ULTRA
  ) {
    isPreferenceDataDefined = false;
  }
  if (isPreferenceDataDefined) {
    //here the clinical preference data is being set from the TpvData
    const iprSize = IPR_OPTIONS.filter((option: Option) =>
      treatmentSpecifications?.tpv?.clinicalPreferences?.iprSize?.includes(
        option.value
      )
    );
    const iprLocation = IPR_LOCATION.filter((option: Option) =>
      treatmentSpecifications?.tpv?.clinicalPreferences?.iprLocation?.includes(
        option.value
      )
    );
    const biteRamp = BITE_RAMP.filter(
      (option: Option) =>
        treatmentSpecifications?.tpv?.clinicalPreferences?.biteRamps ===
        option.value
    );
    const crossbite = PREMOLAR_CROSSBITE.filter(
      (option: Option) =>
        treatmentSpecifications?.tpv?.clinicalPreferences?.premolarCrossbite ===
        option.value
    );
    const elastics = BITE_RAMP.filter(
      (option: Option) =>
        treatmentSpecifications?.tpv?.clinicalPreferences?.extrusionElastics ===
        option.value
    );
    if (isRefinement) {
      if (tpvType === planTypeCode.ZENCLEAR_ULTRA) {
        return {
          ZenClearUltra: {
            others:
              treatmentSpecifications?.tpv?.clinicalPreferences?.others || ''
          }
        };
      }
      return {
        Refinement: {
          iprSize: iprSize,
          iprLocation: iprLocation,
          others: treatmentSpecifications?.tpv?.clinicalPreferences?.others
        }
      };
    } else if (
      (treatmentSpecifications?.treatmentPlanTypes?.[0] ===
        TREATMENT_PLAN_TYPE.ZENCLEAR ||
        treatmentSpecifications?.treatmentPlanTypes?.[0] ===
          TREATMENT_PLAN_TYPE.ZENCLEAR_LITE) &&
      !isRefinement
    ) {
      const iprSizeZenPlus = IPR_OPTIONS.filter((option: Option) =>
        preferenceTemplateData?.ZenPlus?.iprSize?.includes(option.value)
      );
      const iprLocationZenPlus = IPR_LOCATION.filter((option: Option) =>
        preferenceTemplateData?.ZenPlus?.iprLocation?.includes(option.value)
      );
      const biteRampZenPlus = BITE_RAMP.filter(
        (option: Option) =>
          preferenceTemplateData?.ZenPlus?.biteRamps === option.value
      );
      const crossbiteZenPlus = PREMOLAR_CROSSBITE.filter(
        (option: Option) =>
          preferenceTemplateData?.ZenPlus?.premolarCrossbite === option.value
      );
      const elasticsZenPlus = BITE_RAMP.filter(
        (option: Option) =>
          preferenceTemplateData?.ZenPlus?.extrusionElastics === option.value
      );

      return {
        ZenClear: {
          iprSize: iprSize,
          iprLocation: iprLocation,
          others: treatmentSpecifications?.tpv?.clinicalPreferences?.others
        },
        ZenPlus: {
          iprSize: iprSizeZenPlus,
          iprLocation: iprLocationZenPlus,
          biteRamps: biteRampZenPlus?.[0],
          premolarCrossbite: crossbiteZenPlus?.[0],
          extrusionElastics: elasticsZenPlus?.[0],
          others: preferenceTemplateData?.ZenPlus?.others
        },
        ...(treatmentSpecifications?.treatmentPlanTypes?.[0] ===
        TREATMENT_PLAN_TYPE.ZENCLEAR_LITE
          ? {
              ZenClearLite: {
                iprSize: iprSize,
                iprLocation: iprLocation,
                others:
                  treatmentSpecifications?.tpv?.clinicalPreferences?.others
              }
            }
          : {}),
        treatmentPlanType: treatmentSpecifications?.treatmentPlanTypes?.[0]
      };
    } else if (
      treatmentSpecifications?.treatmentPlanTypes?.[0] ===
        TREATMENT_PLAN_TYPE.ZENCLEAR_PLUS &&
      !isRefinement
    ) {
      const iprSizeZenClear = IPR_OPTIONS.filter((option: Option) =>
        preferenceTemplateData?.ZenClear?.iprSize?.includes(option.value)
      );
      const iprLocationZenClear = IPR_LOCATION.filter((option: Option) =>
        preferenceTemplateData?.ZenClear?.iprLocation?.includes(option.value)
      );
      return {
        ZenPlus: {
          iprSize: iprSize,
          iprLocation: iprLocation,
          biteRamps: biteRamp?.[0],
          premolarCrossbite: crossbite?.[0],
          extrusionElastics: elastics?.[0],
          others: treatmentSpecifications?.tpv?.clinicalPreferences?.others
        },
        ZenClear: {
          iprSize: iprSizeZenClear,
          iprLocation: iprLocationZenClear,
          others: preferenceTemplateData?.ZenClear?.others
        },
        ZenClearLite: {
          iprSize: [],
          iprLocation: [],
          others: ''
        },
        treatmentPlanType: ZENPLUS
      };
    } else if (
      treatmentSpecifications?.treatmentPlanTypes?.[0] ===
        TPVFormTypes.ZENCLEAR_ULTRA &&
      !isRefinement
    ) {
      return {
        ZenClearUltra: {
          others:
            treatmentSpecifications?.tpv?.clinicalPreferences?.others ?? ''
        },
        treatmentPlanType: ZENCLEAR_ULTRA
      };
    }
  } else if (!isPreferenceDataDefined && !!preferenceTemplateData) {
    //here the clinical preference data is being set from the preferenceTemplateData
    return setClinicalPreferenceFromPreferenceTemplate(
      preferenceTemplateData,
      isRefinement,
      tpvType
    );
  }
  return {
    //in this there is no clinicalData in TpvData and preferenceTemplate
    ZenClear: undefined,
    ...(!isRefinement && { ZenPlus: undefined }),
    ...(!isRefinement && { treatmentPlanType: undefined })
  };
};
export const getClinicalPreferenceSettingsPage = (
  preferenceTemplateData: Preference | undefined
) => {
  const iprSizeZenPlus = IPR_OPTIONS.filter((option: Option) =>
    preferenceTemplateData?.ZenPlus?.iprSize?.includes(option.value)
  );
  const iprLocationZenPlus = IPR_LOCATION.filter((option: Option) =>
    preferenceTemplateData?.ZenPlus?.iprLocation?.includes(option.value)
  );
  const biteRampZenPlus = BITE_RAMP.filter(
    (option: Option) =>
      preferenceTemplateData?.ZenPlus?.biteRamps === option.value
  );
  const crossbiteZenPlus = PREMOLAR_CROSSBITE.filter(
    (option: Option) =>
      preferenceTemplateData?.ZenPlus?.premolarCrossbite === option.value
  );
  const elasticsZenPlus = BITE_RAMP.filter(
    (option: Option) =>
      preferenceTemplateData?.ZenPlus?.extrusionElastics === option.value
  );
  const iprSizeZenClear = IPR_OPTIONS.filter((option: Option) =>
    preferenceTemplateData?.ZenClear?.iprSize?.includes(option.value)
  );
  const iprLocationZenClear = IPR_LOCATION.filter((option: Option) =>
    preferenceTemplateData?.ZenClear?.iprLocation?.includes(option.value)
  );
  const iprSizeZenPlusRefinement = IPR_OPTIONS.filter((option: Option) =>
    preferenceTemplateData?.ZenPlusRefinement?.iprSize?.includes(option.value)
  );
  const iprLocationZenPlusRefinement = IPR_LOCATION.filter((option: Option) =>
    preferenceTemplateData?.ZenPlusRefinement?.iprLocation?.includes(
      option.value
    )
  );
  const iprSizeZenClearRefinement = IPR_OPTIONS.filter((option: Option) =>
    preferenceTemplateData?.ZenClearRefinement?.iprSize?.includes(option.value)
  );
  const iprLocationZenClearRefinement = IPR_LOCATION.filter((option: Option) =>
    preferenceTemplateData?.ZenClearRefinement?.iprLocation?.includes(
      option.value
    )
  );
  return {
    ZenClear: preferenceTemplateData?.ZenClear
      ? {
          iprLocation: iprLocationZenClear,
          iprSize: iprSizeZenClear,
          others: preferenceTemplateData?.ZenClear?.others
        }
      : undefined,
    ZenPlus: preferenceTemplateData?.ZenPlus
      ? {
          iprSize: iprSizeZenPlus,
          iprLocation: iprLocationZenPlus,
          biteRamps: biteRampZenPlus?.[0],
          premolarCrossbite: crossbiteZenPlus?.[0],
          extrusionElastics: elasticsZenPlus?.[0],
          others: preferenceTemplateData?.ZenPlus?.others
        }
      : undefined,
    ZenPlusRefinement: preferenceTemplateData?.ZenPlusRefinement
      ? {
          iprLocation: iprLocationZenPlusRefinement,
          iprSize: iprSizeZenPlusRefinement,
          others: preferenceTemplateData?.ZenPlusRefinement?.others
        }
      : undefined,
    ZenClearRefinement: preferenceTemplateData?.ZenClearRefinement
      ? {
          iprLocation: iprLocationZenClearRefinement,
          iprSize: iprSizeZenClearRefinement,
          others: preferenceTemplateData?.ZenClearRefinement?.others
        }
      : undefined,
    ZenClearUltra: preferenceTemplateData?.ZenClearUltra
      ? {
          others: preferenceTemplateData?.ZenClearUltra?.others ?? ''
        }
      : undefined,
    ZenClearUltraRefinement: preferenceTemplateData?.ZenClearUltraRefinement
      ? {
          others: preferenceTemplateData?.ZenClearUltraRefinement?.others ?? ''
        }
      : undefined,
    treatmentPlanType: TemplateFormTypes.ZenClear
  };
};
export const planTypeCode = {
  ZEN_PLUS: 'ZENCLEAR_PLUS',
  ZENCLEAR: 'ZENCLEAR',
  ZENCLEAR_LITE: 'ZENCLEAR_LITE',
  ZENCLEAR_ULTRA: 'ZENCLEAR_ULTRA'
};

/**
 * Maps a base treatment type i18n label to its variant that includes
 * aligner set count ranges (e.g. "ZenyumClear™" → "ZenyumClear™ (1 - 20 aligner sets)").
 *
 * For ZenyumClear™, the range shown depends on whether ZenyumClear Lite is
 * available in the clinic's country:
 *  - Lite available: "ZenyumClear™ (11 - 20 aligner sets)"
 *  - Lite not available: "ZenyumClear™ (1 - 20 aligner sets)"
 *
 * Returns the original label unchanged if no mapping exists.
 */
export const mapLabelWithSets = (
  label: string,
  isLiteCountry: boolean
): string => {
  const labelWithSetsMap: Record<string, string> = {
    'prescriptions.zenyumclear_lite':
      'prescriptions.zenyumclear_lite_with_sets',
    'prescriptions.zenyumclear': isLiteCountry
      ? 'prescriptions.zenyumclear_with_sets_lite_available'
      : 'prescriptions.zenyumclear_with_sets',
    'prescriptions.zenyumclear_plus': 'prescriptions.zenyumclear_plus_with_sets'
  };
  return labelWithSetsMap[label] || label;
};

/**
 * Transforms an array of treatment plan type options by replacing their
 * labels with the aligner-set-count variants via `mapLabelWithSets`.
 *
 * Use this to convert base treatment type options (from API or constants)
 * into display-ready options that include set ranges, for use in
 * prescription, referral, and upgrade/downgrade flows.
 */
export const mapOptionsWithSets = <T extends { label: string }>(
  options: T[],
  isLiteCountry: boolean
): T[] =>
  options.map((option) => ({
    ...option,
    label: mapLabelWithSets(option.label, isLiteCountry)
  }));

export const ZENCLEAR_ULTRA_REQUIRED_MAX_COMPLEXITY = 5;

const ZENCLEAR_ULTRA_TREATMENT_PLAN_VALUES = new Set([
  ZenClearUltra,
  TPVFormTypes.ZENCLEAR_ULTRA,
  ZENCLEAR_ULTRA
]);

export const isZenClearUltraAvailable = (
  maxComplexity?: number | null
): boolean => Number(maxComplexity) === ZENCLEAR_ULTRA_REQUIRED_MAX_COMPLEXITY;

export const isZenClearUltraTreatmentPlanValue = (
  value?: string | null
): boolean => !!value && ZENCLEAR_ULTRA_TREATMENT_PLAN_VALUES.has(value);

export const filterZenClearUltraTreatmentPlanOptions = <
  T extends { value: string }
>(
  options: T[],
  maxComplexity?: number | null,
  allowZenClearUltra = true
): T[] => {
  if (allowZenClearUltra && isZenClearUltraAvailable(maxComplexity)) {
    return options;
  }

  return options.filter(
    (option) => !isZenClearUltraTreatmentPlanValue(option.value)
  );
};

export const getTreatmentSpecificationDetails = (
  options: TreatmentSpecificationOptions,
  treatmentPlans: EnumResponse[],
  treatmentSpecifications: TreatmentSpecifications | undefined,
  isLiteCountry = false
): {
  attachmentReuired: string | undefined;
  iprLocation: string | undefined;
  crowdingResolvement: string | undefined;
  planType: string;
  planInstructionEnabled: Record<string, boolean>;
} => {
  const attachmentReuired = options?.getAttachementOptions?.find(
    ({ value }) => value === treatmentSpecifications?.attachmentRequired
  )?.label;
  const iprLocation = options?.getIPRLocations?.find(
    ({ value }) => value === treatmentSpecifications?.iprLocation
  )?.label;
  const crowdingResolvement = options?.getCrowdingTreatmentOptions?.find(
    ({ value }) => value === treatmentSpecifications?.crowdingResolvement
  )?.label;
  const planType = treatmentPlans
    ?.filter(({ value }) =>
      treatmentSpecifications?.treatmentPlanTypes?.find(
        (plan) => plan === value
      )
    )
    ?.map(({ label }) => translate(mapLabelWithSets(label, isLiteCountry)))
    ?.join(', ');
  const planInstructionEnabled = {
    [planTypeCode.ZENCLEAR]:
      !!treatmentSpecifications?.treatmentPlanTypes?.some(
        (plan) => plan === planTypeCode.ZENCLEAR
      ),
    [planTypeCode.ZEN_PLUS]:
      !!treatmentSpecifications?.treatmentPlanTypes?.some(
        (plan) => plan === planTypeCode.ZEN_PLUS
      )
  };
  return {
    attachmentReuired,
    iprLocation,
    crowdingResolvement,
    planType,
    planInstructionEnabled
  };
};

const getOtherTreatmentValue = (
  treatments: string[] | undefined,
  options: EnumResponse[] | undefined
) =>
  treatments?.find((treatment) =>
    options?.every(({ value }) => value !== treatment)
  );

export const getPriorTreatment = (
  options: EnumResponse[] | undefined,
  treatments: string[] | undefined
): string | undefined => {
  if (!treatments) return undefined;
  if (treatments?.length === 0) return translate('prescriptions.none');
  const otherTreatment = getOtherTreatmentValue(treatments, options);
  const treatmentVlaues = options
    ?.filter(({ value }) =>
      treatments?.find((treatment) => treatment === value)
    )
    ?.map(({ label }) => translate(label))
    ?.join(',');
  const finalList: string | undefined =
    treatmentVlaues === ''
      ? otherTreatment
      : `${treatmentVlaues}, ${otherTreatment}`;
  return otherTreatment ? finalList : treatmentVlaues;
};

export const checkIfAnyTeethSelected = (teeths: unknown): boolean => {
  if (!teeths || typeof teeths !== 'object') return false;
  return Object.values(teeths as Record<string, unknown>).some(
    (value) => Array.isArray(value) && value.length > 0
  );
};

export const formatErrorList = (
  error: Record<string, any>,
  maplist: {
    field: string;
    label: string;
    innerFields: {
      field: string;
      label: string;
      animateLabel?: string;
    }[];
    animateLabel?: string;
  }[]
): ErrorList[] => {
  const errorList: ErrorList[] = maplist.reduce(
    (acc: ErrorList[], { field, innerFields, label, animateLabel }) => {
      if (error[field]) {
        if (innerFields.length) {
          const errorFields: ErrorList[] = [];
          innerFields.forEach(
            ({
              field: innerFieled,
              label: innerLabel,
              animateLabel: animateInnerLabel
            }) => {
              return (
                _.get(error, `${field}.${innerFieled}`) &&
                errorFields.push({
                  id: `${field}.${animateInnerLabel ?? innerFieled}`,
                  field,
                  label: `${translate(label)} ${
                    translate(innerLabel) ? '>' : ''
                  } ${translate(innerLabel)}`
                })
              );
            }
          );
          return [...acc, ...errorFields];
        }
        return [
          ...acc,
          {
            id: animateLabel ? animateLabel : field,
            label: translate(label),
            field
          }
        ];
      }
      return acc;
    },
    []
  );
  return errorList;
};

export const getButtonLabel = (activeState: string): ReferralButton => {
  switch (activeState) {
    case REFERRALS_STEPS.PATIENT_FILE:
      return { type: 'submit', label: 'submit_button_rounded_(short).button' };
    case REFERRALS_STEPS.REVIEW_SUMMARY:
      return { type: 'button', label: 'Reworked_No_Show.Black_ghost_button' };
    case REFERRALS_STEPS.PERSONAL_INFO:
    case REFERRALS_STEPS.TREATMENT_SPEC:
    default:
      return { type: 'submit', label: 'button.cta.continue' };
  }
};
export const formatClinicalPreferences = (
  data: TreatmentPLanPreferenceFormType,
  isPrescription: boolean,
  isZenPlus: boolean,
  isZenClear: boolean,
  isZenPlusRefinement: boolean,
  isZenClearRefinement: boolean,
  isRefinementPrescription?: boolean,
  isZenClearLite?: boolean,
  isZenClearUltra?: boolean,
  isZenClearUltraRefinement?: boolean
) => {
  /**
   * Formats the clinical Preference to be send in the api input
   */
  let treatmentPlanData;
  if (isRefinementPrescription) {
    /**
     * This is to handle the refinement data from the prescription
     */
    treatmentPlanData = data?.Refinement;
  } else if (isZenPlus) {
    treatmentPlanData = data?.ZenPlus;
  } else if (isZenClear) {
    treatmentPlanData = data?.ZenClear;
  } else if (isZenClearLite) {
    treatmentPlanData = data?.ZenClearLite;
  } else if (isZenPlusRefinement) {
    /**
     * This is to handle the refinement data from the preference template page
     */
    treatmentPlanData = data?.ZenPlusRefinement;
  } else if (isZenClearRefinement) {
    /**
     * This is to handle the refinement data from the preference template page
     */
    treatmentPlanData = data?.ZenClearRefinement;
  }
  if (isZenClearUltra) {
    const ultraOthers = data?.ZenClearUltra?.others;
    if (isPrescription) {
      return { others: ultraOthers || '' };
    }
    return {
      ZenClearUltra: {
        others: ultraOthers || ''
      }
    };
  }
  if (isZenClearUltraRefinement) {
    const ultraRefinementOthers = data?.ZenClearUltraRefinement?.others;
    return {
      ZenClearUltraRefinement: {
        others: ultraRefinementOthers || ''
      }
    };
  }
  const iprSizeInput = treatmentPlanData?.iprSize?.map((item: Option) => {
    return item?.value as string;
  });
  const iprLocationInput = treatmentPlanData?.iprLocation?.map(
    (item: Option) => {
      return item?.value as string;
    }
  );
  const others = treatmentPlanData?.others;
  if (isPrescription)
    return isZenPlus
      ? {
          iprSize: iprSizeInput || [],
          iprLocation: iprLocationInput || [],
          biteRamps: data?.ZenPlus?.biteRamps?.value,
          premolarCrossbite: data?.ZenPlus?.premolarCrossbite?.value,
          extrusionElastics: data?.ZenPlus?.extrusionElastics?.value,
          others: others || ''
        }
      : {
          iprSize: iprSizeInput || [],
          iprLocation: iprLocationInput || [],
          others: others || ''
        };
  else {
    if (isZenPlus) {
      return {
        ZenPlus: {
          iprSize: iprSizeInput?.sort() || [],
          iprLocation: iprLocationInput || [],
          biteRamps: data?.ZenPlus?.biteRamps?.value,
          premolarCrossbite: data?.ZenPlus?.premolarCrossbite?.value,
          extrusionElastics: data?.ZenPlus?.extrusionElastics?.value,
          others: others || ''
        }
      };
    } else if (isZenClear) {
      return {
        ZenClear: {
          iprSize: iprSizeInput?.sort() || [],
          iprLocation: iprLocationInput || [],
          others: others || ''
        }
      };
    } else if (isZenClearLite) {
      return {
        ZenClearLite: {
          iprSize: iprSizeInput?.sort() || [],
          iprLocation: iprLocationInput || [],
          others: others || ''
        }
      };
    } else if (isZenPlusRefinement) {
      return {
        ZenPlusRefinement: {
          iprSize: iprSizeInput?.sort() || [],
          iprLocation: iprLocationInput || [],
          others: others || ''
        }
      };
    } else if (isZenClearRefinement) {
      return {
        ZenClearRefinement: {
          iprSize: iprSizeInput?.sort() || [],
          iprLocation: iprLocationInput || [],
          others: others || ''
        }
      };
    }
  }
};

export const formatTreatmentSpecificationsForRefinementExternal = (
  refinementFormType: string,
  isResetClicked: boolean,
  isAutoSave: boolean,
  ClinicalPreferences: clinicalPreferencesRefinementType,
  treatmentSpecification: TreatmentSpecRefinementType,
  alignerAndIPRInfo?: AlignerIPRInfo
) => {
  const formData = {
    [planTypeCode.ZEN_PLUS]: treatmentSpecification?.ZenPlus,
    [planTypeCode.ZENCLEAR]: treatmentSpecification?.ZenClear,
    [planTypeCode.ZENCLEAR_LITE]: {
      ...treatmentSpecification?.ZenClear,
      formType: TPVFormTypes.ZENCLEAR_LITE
    }
  };

  const selectedFormData =
    formData[refinementFormType as keyof typeof formData];

  const treatmentSpecifications: TreatmentSpecifications = {
    treatmentPlanTypes: [refinementFormType],
    tpv: {
      ...getRefinementFormData(
        selectedFormData as RefinementFormType,
        refinementFormType === planTypeCode.ZEN_PLUS,
        alignerAndIPRInfo
      ),
      clinicalPreferences: isResetClicked
        ? {}
        : formatClinicalPreferences(
            ClinicalPreferences,
            true,
            false,
            false,
            false,
            false,
            true
          )
    },
    version: !isAutoSave ? 'V1' : undefined
  };
  return { treatmentSpecifications };
};

export const formatTreatmentSpecificationsForRefinementUltraExternal = (
  isResetClicked: boolean,
  isAutoSave: boolean,
  clinicalPreferences: clinicalPreferencesRefinementType,
  currentStage: Parameters<typeof buildUltraRefinementTpvPayload>[0],
  treatmentSpecification: TreatmentSpecRefinementType,
  totalAlignerSet?: number
) => {
  const treatmentSpecifications: TreatmentSpecifications = {
    treatmentPlanTypes: [planTypeCode.ZENCLEAR_ULTRA],
    tpv: buildUltraRefinementTpvPayload(
      currentStage,
      treatmentSpecification?.ZenClearUltra,
      isResetClicked ? undefined : clinicalPreferences,
      totalAlignerSet
    ) as unknown as TreatmentSpecifications['tpv'],
    version: !isAutoSave ? 'V1' : undefined
  };
  return { treatmentSpecifications };
};

const getAlignerInput = (
  isSet: boolean,
  alignerData?: {
    radioValue: any;
    inputValue: any;
  },
  alignerAndIPRInfo?: AlignerIPRInfo
) => {
  if (isSet)
    return alignerData?.radioValue === ALIGNER_INFO_TYPE.SET &&
      alignerData?.inputValue
      ? alignerData?.inputValue?.split('/')?.[0]
      : getSetAndZenStageDefaultValues(
          alignerAndIPRInfo?.alignerInfo?.currentAligner,
          alignerAndIPRInfo?.alignerInfo?.totalSetAligner
        ).set;
  return ZENSTAGE_OPTIONS?.find(
    (option) =>
      option?.value ===
      (alignerData?.radioValue === ALIGNER_INFO_TYPE.ZEN_STAGE
        ? alignerData?.inputValue
        : `${
            getSetAndZenStageDefaultValues(
              alignerAndIPRInfo?.alignerInfo?.currentAligner,
              alignerAndIPRInfo?.alignerInfo?.totalSetAligner
            ).zenstage
          }`)
  );
};
export const getRefinementSpecialNotes = (
  isSubmitted: boolean,
  TreatmentSpecifications?: TreatmentSpecifications,
  isZenPlus?: boolean
) => {
  if (isSubmitted || TreatmentSpecifications?.tpv?.specialNotes) {
    return TreatmentSpecifications?.tpv?.specialNotes || '';
  } else {
    if (isZenPlus) return TreatmentSpecifications?.zenplusInstructions || '';
    else return TreatmentSpecifications?.treatmentPlanInstructions || '';
  }
};
export const getRefinementDefaultTreatmentSpec = (
  isSubmitted: boolean,
  tpvType?: string,
  TreatmentSpecifications?: TreatmentSpecifications,
  alignerAndIPRInfo?: AlignerIPRInfo
) => {
  if (tpvType === planTypeCode.ZENCLEAR_ULTRA) {
    return {
      ZenClearUltra:
        hasUltraRefinementSubmissionReason(
          TreatmentSpecifications?.tpv as UltraRefinementTpvType
        ) || isSubmitted
          ? getUltraRefinementPrescriptionSavedValues(
              TreatmentSpecifications?.tpv as UltraRefinementTpvType
            )
          : {
              toothInformation: {
                information: '',
                dentitionSelection: [],
                toothChart: undefined,
                primaryToothChart: undefined
              },
              dentitionSpace: { space: '', toothChart: undefined },
              toothMovementRestriction: {
                restriction: '',
                dentitionSelection: [],
                toothChart: undefined,
                primaryToothChart: undefined
              },
              toothAttachment: {
                attachment: '',
                dentitionSelection: [],
                toothChart: undefined,
                primaryToothChart: undefined
              },
              specialNotes:
                TreatmentSpecifications?.treatmentPlanInstructions || ''
            },
      treatmentType: 'ZenClearUltra'
    };
  }
  const data = TreatmentSpecifications?.tpv;
  const refinementGoals: Option[] = [];
  data?.refinementGoals?.forEach((element: any) => {
    const option = REFINEMENT_GOALS_OPTIONS?.find(
      (item) => item?.value === element?.name
    );
    if (option) refinementGoals?.push(option);
  });
  const toothChart2: Record<string, string> = {};
  data?.refinementGoals
    ?.find(
      (option: any) => option?.name === REFINEMENT_GOALS.CLOSE_RESIDUAL_SPACES
    )
    ?.value?.forEach((item: any) => {
      toothChart2[item?.position] = item?.value.slice(0, -2);
    });

  const alignerUpperData = getAlignerData(data?.alignerInfo || [], 'upper');
  const alignerLowerData = getAlignerData(data?.alignerInfo || [], 'lower');

  const toothChart3 =
    data?.iprInfo?.name === REFINEMENT_IPR.NOT_AS_PRESCRIBED
      ? data?.iprInfo?.value?.map((item: any) => item?.position) || []
      : [];

  const attachmentsData = data?.refinementGoals
    ?.find(
      (option) => option?.name === REFINEMENT_GOALS.RESOLVE_POSTERIOR_OPEN_BITE
    )
    ?.value?.find(
      (item) =>
        item?.name === RESOLVE_POSTERIOR_OPEN_BITE_OPTIONS.ATTACHMENTS_ONLY ||
        item?.name === RESOLVE_POSTERIOR_OPEN_BITE_OPTIONS.SEE_RESULTING_PLAN
    );
  const buttonsForElasticsData = data?.refinementGoals
    ?.find(
      (option) => option?.name === REFINEMENT_GOALS.RESOLVE_POSTERIOR_OPEN_BITE
    )
    ?.value?.find(
      (item) =>
        item?.name ===
        RESOLVE_POSTERIOR_OPEN_BITE_OPTIONS.ADD_BUTTON_FOR_ELASTICS
    );

  const newdata = {
    reasonForSubmission:
      ZENCLEAR_TPV_REFINEMENT_FORM_REASON_WITH_ILL_FITTING?.find(
        (reason) =>
          ZENCLEAR_TPV_REFINEMENT_FORM_REASON_BACKEND[reason?.value] ===
          data?.submissionReason
      ) || ZENCLEAR_TPV_REFINEMENT_FORM_REASON_WITH_ILL_FITTING[0],
    archesToTreat:
      Number(data?.archesToTreat?.length) > 1
        ? ZENCLEAR_TPV_REFINEMENT_FORM_ARCHES_TO_TREAT[2]
        : ZENCLEAR_TPV_REFINEMENT_FORM_ARCHES_TO_TREAT?.find(
            (option) => option?.value === data?.archesToTreat?.[0]
          ) || ZENCLEAR_TPV_REFINEMENT_FORM_ARCHES_TO_TREAT[2],
    attachments:
      ZENCLEAR_TPV_REFINEMENT_FORM_ATTACHMENTS?.find(
        (option) => option?.value === data?.attachments
      ) || ZENCLEAR_TPV_REFINEMENT_FORM_ATTACHMENTS[0],
    iprPerformed:
      alignerAndIPRInfo?.iprInfo?.length === 0
        ? IPR_REFINEMENT_FORM_OPTIONS[0]
        : IPR_REFINEMENT_FORM_OPTIONS?.find(
            (option) => option?.value === data?.iprInfo?.name
          ) || IPR_REFINEMENT_FORM_OPTIONS[0],
    refinementGoals,
    specialNotes: getRefinementSpecialNotes(
      isSubmitted,
      TreatmentSpecifications,
      tpvType === planTypeCode.ZEN_PLUS
    ),
    radioUpper: alignerUpperData?.radioValue || ALIGNER_INFO_TYPE.SET,
    radioLower: alignerLowerData?.radioValue || ALIGNER_INFO_TYPE.SET,
    alignerInputUpperSet: getAlignerInput(
      true,
      alignerUpperData,
      alignerAndIPRInfo
    ),

    alignerInputLowerSet: getAlignerInput(
      true,
      alignerLowerData,
      alignerAndIPRInfo
    ),

    alignerInputUpperZenstage: getAlignerInput(
      false,
      alignerUpperData,
      alignerAndIPRInfo
    ),
    alignerInputLowerZenstage: getAlignerInput(
      false,
      alignerLowerData,
      alignerAndIPRInfo
    ),
    toothChart2,
    toothChart3,
    ...(tpvType === planTypeCode.ZEN_PLUS && {
      buttonsForElastics:
        buttonsForElasticsData?.name ===
        RESOLVE_POSTERIOR_OPEN_BITE_OPTIONS.ADD_BUTTON_FOR_ELASTICS
          ? formatTeethsForController(buttonsForElasticsData?.value)
          : undefined
    }),
    attachmentsOnly:
      attachmentsData?.name ===
      RESOLVE_POSTERIOR_OPEN_BITE_OPTIONS.ATTACHMENTS_ONLY
        ? formatTeethsForController(attachmentsData?.value)
        : undefined,
    attachmentsSelect:
      ATTACHMENTS_OPTIONS?.find(
        (option) => option?.value === attachmentsData?.name
      ) || ATTACHMENTS_OPTIONS[0],
    designId: data?.designId,
    iprInfoText: data?.iprInfoText || '',
    formType: tpvType,
    attachmentsOnlyCheck: !!data?.attachmentsOnlyCheck,
    ...(tpvType === planTypeCode.ZEN_PLUS && {
      addButtonsForElasticsCheck: !!data?.addButtonsForElasticsCheck
    })
  };
  if (tpvType === planTypeCode.ZEN_PLUS)
    return {
      ZenPlus: newdata,
      ZenClear: undefined,
      treatmentType: TYPES.ZENCLEAR_PLUS
    };
  else
    return {
      ZenPlus: undefined,
      ZenClear: newdata,
      treatmentType:
        tpvType === planTypeCode.ZENCLEAR_LITE
          ? TYPES.ZENCLEAR_LITE
          : TYPES.ZENCLEAR
    };
};
export const getCurrentDeliveryStage = (
  alignerProductionInfo?: AlignerProductionType
) => {
  return (
    alignerProductionInfo?.deliveryObject?.statusLabel ||
    alignerProductionInfo?.productionRecord?.statusLabel
  );
};
